<template>
    <div class="flex-embed">
        <!-- Outside the error branch on purpose. A portal thread's PrescriberNPI has no
             foreign key to Prescribers, so an NPI with no Hub record yields zero patients
             and zero prescribers, errorMessage becomes "No Records Found", and nesting
             this inside the results card left the agent with the empty CRM panel this
             link exists to replace. The thread id came from the task, so it is known to
             be good regardless of what the lookups returned. -->
        <b-alert
            v-if="routeThreadId"
            show
            variant="info"
            class="py-2 mb-2 d-flex align-items-center">
            <i class="fa fa-comments mr-2"></i>
            <span class="mr-auto">Portal chat</span>
            <!-- Opens the top window rather than navigating with the router. This page is
                 the Flex CRM iframe, and the queue lives under a route whose layout is the
                 full Hub chrome, so routing here would replace the embed with a sidebar,
                 header and table inside a ~400px panel, with no way back for the rest of
                 the task. Gated on the permission the destination requires, so a link is
                 never offered that the navigation guard would bounce. -->
            <b-button
                v-if="canOpenThreadQueue"
                size="sm"
                variant="primary"
                @click="openThreadInHub">
                Open conversation history
            </b-button>
        </b-alert>

        <div v-if="errorMessage?.length" id="error-display">
            <b-card :title="errorMessage" class="text-center" />
        </div>
        <template v-else>
            <b-card v-if="!dataLoaded">
                <div class="text-center p-4">
                    <b-spinner variant="primary"></b-spinner>
                    <p class="mt-2">Loading...</p>
                </div>
            </b-card>
            <b-card v-else>
                <b-tabs pills card small class="">
                    <b-tab v-for="(patient, idx) in patients" :key="'patient-' + patient.id" lazy @click="updateCallLogPatient(patient.id)">
                        <template #title>
                            <div>
                                <strong>{{ patient.displayNamesForPerson() }}</strong> <small>({{ patient.id }})</small>
                            </div>
                            <div>
                                <small>
                                    {{ patient.dateOfBirth | formatLocalDate("MM-DD-YYYY") }}
                                    (age: {{ patient.age }})
                                </small>
                            </div>
                        </template>
                        <div>
                            <EntityAlertsComponent :patient="patients[idx]" />
                            <b-row>
                                <b-col>
                                    <PatientDemographicsComponent v-model="patients[idx]" />
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <EmergencyContactList :id="'flexEmergencyContacts-' + patient.id" :patient="patient" :read-only="true" />
                                </b-col>
                            </b-row>
                            <b-row class="mt-3">
                                <b-col>
                                    <card cardBodyClasses="p-0">
                                        <b-row>
                                            <b-col>
                                                <h4 class="card-subtitle" style="padding-top: 15px; padding-left: 15px; padding-bottom: 15px">
                                                    Patient Notes
                                                </h4>
                                            </b-col>
                                        </b-row>
                                        <NotesList :entity-id="+patient.id" :entity-type="EntityType.Patient" :paging="true" :page-size="5" empty-text="No Patient Memos on File" />
                                    </card>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <b-button variant="info"
                                        :to="{ name: 'PatientDashboardFlex', params: { id: +patient.id } }"
                                        class="mt-4">
                                        To Patient Dashboard
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>
                    <b-tab v-for="(prescriber, idx) in prescribers" :key="'prescriber-' + prescriber.id" lazy
                        title-item-class="prescriber-tab">
                        <template #title>
                            <div>
                                <strong>{{ prescriber.displayNamesForPerson() }}</strong>
                            </div>
                            <div>
                                <small>
                                    <template v-if="prescriber.practiceName || prescriber.npiNumber">
                                        <span v-if="prescriber.practiceName">{{ prescriber.practiceName }}</span>
                                        <span v-if="prescriber.npiNumber">(NPI: {{ prescriber.npiNumber }})</span>
                                    </template>
                                    <template v-else>&nbsp;</template>
                                </small>
                            </div>
                        </template>
                        <div>
                            <EntityAlertsComponent :prescriber="prescribers[idx]" />
                            <b-row>
                                <b-col>
                                    <PrescriberDemographicsComponent v-model="prescribers[idx]" />
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <b-button variant="primary"
                                        :to="{ name: 'PrescriberDetailsFlex', params: { id: +prescriber.id } }"
                                        class="mt-4 mr-2">
                                        To Prescriber Details
                                    </b-button>
                                    <b-button variant="info"
                                        :to="{ name: 'DoctorDashboardFlex', query: { doctorSearch: prescriber.npiNumber } }"
                                        class="mt-4">
                                        To Doctor Dashboard
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>
                </b-tabs>
            </b-card>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { Patient } from '@/models/Patient';
import { Prescriber } from '@/models/Prescriber';
import axios, { AxiosError } from "axios";
import PatientDemographicsComponent from "@/components/Patient/PatientDemographicsComponent.vue";
import PrescriberDemographicsComponent from "@/components/Prescriber/PrescriberDemographicsComponent.vue";
import EntityAlertsComponent from "@/components/EntityAlertsComponent.vue";
import EmergencyContactList from "@/components/Patient/EmergencyContactList.vue";
import NotesList from "@/components/Note/NotesList.vue";
import { EntityType } from "@/models/EntityType";

@Component({
    name: "FlexEmbed",
    components: {
        EntityAlertsComponent,
        PatientDemographicsComponent,
        PrescriberDemographicsComponent,
        EmergencyContactList,
        NotesList
    }
})
export default class FlexEmbed extends Vue {
    protected patients: Patient[] = [];
    protected prescribers: Prescriber[] = [];
    private patientError: string = "";
    private prescriberError: string = "";
    private hasInitiallyUpdated: boolean = false;
    private dataLoaded: boolean = false;
    EntityType = EntityType;

    get routePhoneNumber(): string | undefined | null {
        return this.$route.params?.phoneNumber;
    }

    get routeCallSid(): string | undefined | null {
        return this.$route.params?.callSid;
    }

    /**
     * A prescriber portal chat identifies the caller by NPI, not by phone number — the task
     * has no phone number at all. Present only on the prescriber-keyed route.
     */
    get routePrescriberNpi(): string | undefined | null {
        return this.$route.params?.npi;
    }

    get routeThreadId(): number | null {
        const raw = this.$route.params?.threadId;
        const parsed = parseInt(raw ?? "", 10);
        return isNaN(parsed) ? null : parsed;
    }

    /**
     * Whether this agent may actually reach the thread queue. The destination carries a
     * permission, and the guard redirects anyone without it to /unauthorized — which,
     * from inside the CRM iframe, would render there. Asking the same question the guard
     * asks keeps the link honest.
     */
    get canOpenThreadQueue(): boolean {
        return this.$canNavigateTo(this.threadQueueLocation);
    }

    private get threadQueueLocation() {
        return {
            path: "/maintenance/provider-communications",
            query: { threadId: String(this.routeThreadId) },
        };
    }

    /**
     * Opens the thread in the Hub proper, in a new top-level tab.
     *
     * Deliberately not a router push: this component is the Flex CRM panel, and the
     * queue's route renders the full Hub layout, which does not belong in a narrow
     * iframe the agent cannot navigate back out of.
     */
    openThreadInHub(): void {
        const href = this.$router.resolve(this.threadQueueLocation).href;
        window.open(href, "_blank", "noopener");
    }

    get hasAnyResults(): boolean {
        return this.patients.length > 0 || this.prescribers.length > 0;
    }

    get errorMessage(): string {
        if (!this.hasAnyResults && (this.patientError || this.prescriberError)) {
            return "No Records Found";
        }
        return "";
    }

    async mounted() {
        await Promise.all([
            this.fetchPatient(),
            this.fetchPrescriber()
        ]);

        this.dataLoaded = true;

        this.$nextTick(() => {
            this.handleInitialCallLogUpdate();
        });
    }

    async fetchPatient() {
        try {
            if (!this.routePhoneNumber) {
                throw "No Active Conversation";
            }
            const response = await axios.get<Patient[]>(`/Patient/flex/${this.routePhoneNumber}`);
            this.patients = response.data.map((p: any) => new Patient(p));
            this.patientError = "";
        } catch (err) {
            if (!err) {
                this.patientError = "Error occurred loading patient information.";
                return;
            }

            const axiosError = (err as AxiosError);

            if (axiosError.response?.data) {
                this.patientError = axiosError.response.data as any;
                return;
            }

            this.patientError = (err as any).toString();
        }
    }

    async fetchPrescriber() {
        try {
            // The NPI route wins when present: a portal chat carries an NPI and no phone
            // number, so the phone lookup would return nothing for it.
            const url = this.routePrescriberNpi
                ? `/Prescriber/flex/by-npi/${this.routePrescriberNpi}`
                : this.routePhoneNumber
                    ? `/Prescriber/flex/${this.routePhoneNumber}`
                    : null;

            if (!url) {
                return;
            }
            const prescribers = await axios.get<Prescriber[]>(url);
            this.prescribers = prescribers.data.map(p => new Prescriber(p));
            this.prescriberError = "";
        } catch (err) {
            const axiosError = (err as AxiosError);
            if (axiosError.response?.data) {
                this.prescriberError = axiosError.response.data as any;
            } else {
                this.prescriberError = err ? (err as any).toString() : "Error loading prescribers";
            }
            console.log("No prescribers found or error fetching prescribers", err);
        }
    }

    handleInitialCallLogUpdate() {
        if (this.hasInitiallyUpdated) {
            return;
        }

        if (this.patients.length) {
            this.hasInitiallyUpdated = true;
            this.updateCallLogPatient(this.patients[0].id);
        }
    }

    async updateCallLogPatient(patientId: number) {
        try {
            const callSid = this.routeCallSid;
            const phoneNumber = this.routePhoneNumber;

            if (!phoneNumber) {
                return;
            }

            const requestBody: any = {
                patientId: patientId
            };

            if (callSid && callSid.trim() !== '') {
                requestBody.callSid = callSid;
            }
            else {
                requestBody.externalNumber = phoneNumber;
            }

            await axios.patch(
                '/api/Call/update-patient',
                requestBody
            );
        } catch (err) {
            console.error('Error updating CallLog PatientId:', err);
        }
    }
}
</script>

<style scoped>
:deep(.modal-dialog) {
    max-width: 95%;
}

::v-deep(.nav-pills.nav-pills:not(.flex-column) .nav-item:not(:last-child) .nav-link) {
    margin-right: 6px;
    margin-bottom: 6px;

}

::v-deep(.nav-link) {
    font-size: 1.05em;
    line-height: 1.25;
    padding: 7px 16px !important;
    border: 1px solid transparent;
}

::v-deep(.card) {
    font-size: 1.2em;
}

::v-deep(.tab-content)
{
    display: block !important;
}

/* Prescriber-specific tab styling */
::v-deep(.prescriber-tab .nav-link) {
    background: #dceafc !important;
    border: 1px solid #67a9ff;
    color: white;
}

::v-deep(.prescriber-tab .nav-link:hover) {
    background: #3b82f6;
}

::v-deep(.prescriber-tab .nav-link.active) {
    background: #1e40af !important;
    box-shadow: 0 4px 6px rgba(30, 64, 175, 0.3);
}
</style>
